import React, { useMemo } from "react";
import { Helmet } from "react-helmet";
import { useColorMode } from "@chakra-ui/react";
import { FaMapMarkerAlt, FaPhoneAlt, FaEnvelope, FaShieldAlt, FaArrowRight, FaBuilding } from "react-icons/fa";
import { motion } from "framer-motion";

const Agence = () => {
  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";

  const allAgencesData = [
    { ville: "Paris", dep: "75", adresse: "15 boulevard Pereire, 75017 Paris" },
    { ville: "La Courneuve", dep: "93", adresse: "8 place Georges Braque, 93120 La Courneuve", isSiege: true },
    { ville: "Saint-Denis", dep: "93", adresse: "187 Boulevard Anatole France, 93200 Saint-Denis", isFeatured: true },
    { ville: "Lille", dep: "59", adresse: "69 rue Gambetta, 59830 Cysoing" },
    { ville: "Colombes", dep: "92", adresse: "91 Rue Gabriel Péri, 92700 Colombes" },
    { ville: "Nangis", dep: "77", adresse: "19 rue des Fontaines, 77370 Nangis" },
    { ville: "Domont", dep: "95", adresse: "2 allées des Dentelieres, 95330 Domont" },
  ];

  const sortedAgences = useMemo(() => {
    return [...allAgencesData].sort((a, b) => a.dep.localeCompare(b.dep));
  }, []);

  const featuredAgence = sortedAgences.find(a => a.isFeatured);
  const otherAgences = sortedAgences.filter(a => !a.isFeatured);

  const contact = {
    phone: "01 43 52 64 23",
    email: "his@donnerlavieauxannees.com",
  };

  // Définition des variants d'animation
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
  };

  const sectionVariants = {
    hidden: { opacity: 0, scale: 0.98 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.6 } }
  };

  return (
    <div className={`min-h-screen overflow-hidden transition-colors duration-300 ${isDark ? 'bg-[#0a0f16]' : 'bg-[#FAFCFF]'}`}>
      <Helmet>
        <title>Nos Agences — Hetep Iaout Services | Aide à domicile Île-de-France</title>
        <meta name="description" content="Retrouvez les agences Hetep Iaout Services en Île-de-France. Services d'aide à domicile certifiés Cap'Handéo près de chez vous. Devis gratuit sous 24h." />
        <link rel="canonical" href="https://www.hisvie.com/agences/" />
      </Helmet>

      {/* --- HERO SECTION --- */}
      <div className="relative pt-28 pb-12 md:pt-20 md:pb-20 px-4 md:px-6 max-w-7xl mx-auto">
        <motion.div 
          className="w-full md:w-2/3 relative z-20"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-100 mb-8">
            <FaShieldAlt className="text-blue-600 text-sm" />
            <span className="text-blue-800 text-xs font-bold tracking-widest uppercase">Réseau Certifié</span>
          </div>

          <h1 className={`text-4xl md:text-7xl font-black leading-[1.1] mb-6 ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>
            Nos Agences. <br className="hidden md:block"/>
            <span className="text-blue-600 font-light italic font-serif">Proches de vous.</span>
          </h1>
        </motion.div>
      </div>

      {/* --- GRID AGENCES --- */}
      <motion.section 
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
        className="max-w-7xl mx-auto px-4 md:px-6 pb-20 relative z-30"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          
          {/* --- CARTE : AGENCE PRINCIPALE (SAINT-DENIS EN GROS) --- */}
          <motion.a
            variants={itemVariants}
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(featuredAgence.adresse)}`}
            target="_blank"
            className={`md:col-span-2 group relative flex flex-col md:flex-row p-8 md:p-12 rounded-[2.5rem] overflow-hidden transition-all duration-500 shadow-xl border-2 ${
              isDark ? 'bg-blue-900/20 border-blue-500/30' : 'bg-white border-blue-600'
            }`}
          >
            <div className="flex-1 relative z-10">
                <div className="flex items-center gap-3 mb-6">
                    <div className="px-3 py-1 rounded-lg bg-blue-600 text-white text-xs font-bold uppercase tracking-tighter flex items-center gap-2">
                        <FaBuilding size={10} /> Agence Principale
                    </div>
                    <span className={`text-xs font-bold px-2 py-1 rounded border ${isDark ? 'border-blue-400/30 text-blue-400' : 'border-blue-100 text-blue-600'}`}>
                        Département {featuredAgence.dep}
                    </span>
                </div>
                
                <h2 className={`text-3xl md:text-5xl font-black mb-4 ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>
                    {featuredAgence.ville}
                </h2>
                <p className={`text-lg mb-8 max-w-sm ${isDark ? 'text-blue-100/60' : 'text-gray-500'}`}>
                    {featuredAgence.adresse}
                </p>
                <div className="flex items-center gap-2 font-bold text-blue-600 group-hover:gap-4 transition-all">
                    Itinéraire vers l'agence <FaArrowRight size={12} />
                </div>
            </div>
          </motion.a>

          {/* --- LES AUTRES AGENCES --- */}
          {otherAgences.map((agence, index) => (
            <motion.a
              key={index}
              variants={itemVariants}
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(agence.adresse)}`}
              target="_blank"
              className={`group relative flex flex-col p-8 rounded-[2rem] transition-all duration-500 border ${
                isDark 
                  ? 'bg-white/5 border-white/10 hover:bg-white/10' 
                  : 'bg-white border-gray-100 hover:border-blue-100 shadow-sm hover:shadow-lg'
              }`}
            >
              <div className="flex justify-between items-start mb-6">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    isDark ? 'bg-blue-500/10 text-blue-400' : 'bg-blue-50 text-blue-600'
                }`}>
                    <FaMapMarkerAlt size={20} />
                </div>
                <div className="flex flex-col items-end gap-2">
                    <span className={`text-[10px] font-black px-2 py-1 rounded-md uppercase tracking-widest ${
                        isDark ? 'bg-gray-800 text-gray-400' : 'bg-gray-50 text-gray-400 border border-gray-100'
                    }`}>
                        {agence.dep}
                    </span>
                    {agence.isSiege && (
                        <span className="text-[10px] font-black px-2 py-1 rounded-md uppercase tracking-widest bg-blue-600 text-white shadow-md">
                            Siège Social
                        </span>
                    )}
                </div>
              </div>

              <h2 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>
                {agence.ville}
              </h2>
              <p className={`text-sm leading-relaxed mb-6 flex-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {agence.adresse}
              </p>
              <div className={`flex items-center gap-2 text-xs font-bold transition-all ${isDark ? 'text-yellow-400 group-hover:text-yellow-300' : 'text-blue-600 group-hover:text-blue-800'}`}>
                Voir l'itinéraire <FaArrowRight size={10} />
              </div>
            </motion.a>
          ))}
        </div>
      </motion.section>

      {/* --- CONTACT SECTION --- */}
      <motion.section 
          initial="hidden" 
          whileInView="visible" 
          viewport={{ once: true }} 
          variants={sectionVariants}
          className="max-w-6xl mx-auto px-4 pb-24"
      >
          <div className={`relative overflow-hidden rounded-[2.5rem] p-8 md:p-14 flex flex-col lg:flex-row items-center gap-10 border shadow-2xl ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100'}`}>
              <div className="flex-1 z-10 text-center md:text-left">
                  <h2 className={`text-3xl md:text-5xl font-black mb-4 tracking-tighter leading-tight ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>
                      Une question <span className="text-blue-600 font-serif italic font-light">locale ?</span>
                  </h2>
                  <p className={`text-base md:text-lg max-w-md mx-auto md:mx-0 font-light ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      Nos experts en région vous répondent et vous accompagnent dans toutes vos démarches de proximité.
                  </p>
              </div>

              <div className="flex flex-col sm:flex-row w-full lg:w-auto gap-4 z-10 shrink-0">
                  <a href={`tel:${contact.phone.replace(/\s/g, '')}`} className="w-full sm:w-auto">
                      <button className="flex items-center justify-center gap-3 bg-blue-600 text-white px-8 py-4 rounded-2xl font-black uppercase text-[11px] tracking-widest hover:bg-blue-700 transition-all w-full shadow-lg shadow-blue-600/20">
                          <FaPhoneAlt size={14} /> {contact.phone}
                      </button>
                  </a>
                  
                  <motion.a 
                      href={`mailto:${contact.email}`} 
                      whileHover={{ scale: 1.02 }} 
                      whileTap={{ scale: 0.98 }}
                      className={`flex items-center justify-center gap-3 px-8 py-4 rounded-2xl font-black uppercase text-[11px] tracking-widest transition-all border w-full sm:w-auto ${
                          isDark 
                          ? 'bg-gray-800 text-gray-300 border-gray-700 hover:bg-gray-700' 
                          : 'bg-blue-50 text-blue-800 border-blue-100 hover:bg-blue-100'
                      }`}
                  >
                      <FaEnvelope size={14} /> Nous écrire
                  </motion.a>
              </div>
          </div>
      </motion.section>
    </div>
  );
};

export default Agence;